/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Tab,
    Nav,
    Row,
    Col,
    Table,
    Button,
    Modal,
    Card,
    Container,
    InputGroup,
    FormControl,
    Form
} from 'react-bootstrap';
import Chart from 'react-apexcharts';
import {
    FaUsers,
    FaCreditCard,
    FaUserCheck,
    FaShoppingCart,
    FaStar,
    FaBolt,
    FaMobileAlt,
    FaSearch,
    FaDownload,
    FaCalendarAlt,
    FaEye,
    FaLock,
    FaUnlock,
    FaUserSlash,
    FaLaptop,
    FaChartLine
} from 'react-icons/fa';

// Mock data removed in favor of real API data

// --- Components ---

const StatCard = ({ title, value, icon: Icon, colorClass }) => (
    <Card className="h-100 shadow-sm border-0 rounded-4 overflow-hidden">
        <Card.Body className="d-flex align-items-center justify-content-between">
            <div>
                <p className="text-dark font-medium font-weight-semibold mb-0 ">{title}</p>
                <h5 className="mb-0 font-weight-semibold mt-1">{value}</h5>
            </div>
            <div className={`p-3 rounded-3 bg-opacity-25 ${colorClass}`}>
                <Icon className={colorClass.replace('bg-', 'text-')} size={24} />
            </div>
        </Card.Body>
    </Card>
);

const FilterBar = ({
    title,
    onSearch,
    onStartDateChange,
    onEndDateChange,
    onExport
}) => (
    <Row className="mb-4 align-items-center">
        <Col lg={12} md={12} className="mb-3">
            <h4 className="mb-0 font-weight-bold">{title}</h4>
        </Col>
        <Col lg={12} md={12} className="mb-3 card p-3">
            <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <InputGroup className="rounded-3 border border-dark" style={{ maxWidth: '250px' }}>
                    <InputGroup.Text className="bg-light border-0"><FaSearch className="text-muted" /></InputGroup.Text>
                    <FormControl
                        placeholder="Search..."
                        className="bg-light border-0"
                        onChange={(e) => onSearch(e.target.value)}
                    />
                </InputGroup>
                <div className="d-flex align-items-center border border-dark px-2 rounded">
                    <span className="small text-muted me-2 ">FROM:</span>
                    <FormControl
                        type="date"
                        className="bg-transparent border-0 small"
                        style={{ width: 'auto' }}
                        onChange={(e) => onStartDateChange(e.target.value)}
                    />
                </div>
                <div className="d-flex align-items-center border border-dark px-2 rounded">
                    <span className="small text-muted me-2 ">TO:</span>
                    <FormControl
                        type="date"
                        className="bg-transparent border-0 small"
                        style={{ width: 'auto' }}
                        onChange={(e) => onEndDateChange(e.target.value)}
                    />
                </div>
                <Button variant="primary" className="d-flex align-items-center gap-2 rounded-3 " onClick={onExport}>
                    <FaDownload /> Export
                </Button>
            </div>
        </Col>
    </Row>
);

export default function App() {
    const [activeKey, setActiveKey] = useState('status');
    const [selectedCart, setSelectedCart] = useState(null);

    const [summary, setSummary] = useState({});
    const [astrologers, setAstrologers] = useState([]);
    const [carts, setCarts] = useState([]);
    const [userReports, setUserReports] = useState([]);
    const [userGrowth, setUserGrowth] = useState([]);
    const [userGrowthSummary, setUserGrowthSummary] = useState(null);
    const [astroPerformance, setAstroPerformance] = useState([]);

    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const limit = 10;

    const tokenConfig = {
        headers: { 'Authorization': `${localStorage.getItem("vedhvidhi-admin-token")}` }
    };

    const fetchAstrologers = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/online-astrologers?search=${search}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`, tokenConfig);
            if (res.data?.data) {
                setAstrologers(res.data.data);
                setTotalPages(res.data.paginationDetail?.totalPages || res.data.totalPages || res.data.pagination?.totalPages || 1);
            }
            else if (Array.isArray(res.data)) setAstrologers(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchSummary = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/summary`, tokenConfig);
            if (res.data?.data) setSummary(res.data.data);
            else if (res.data) setSummary(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchCarts = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/cart-abandoned?search=${search}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`, tokenConfig);
            if (res.data?.data) {
                setCarts(res.data.data);
                setTotalPages(res.data.paginationDetail?.totalPages || res.data.totalPages || res.data.pagination?.totalPages || 1);
            }
            else if (Array.isArray(res.data)) setCarts(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchUserReports = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/user-recharge-report?search=${search}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`, tokenConfig);
            if (res.data?.data) {
                setUserReports(res.data.data);
                setTotalPages(res.data.paginationDetail?.totalPages || res.data.totalPages || res.data.pagination?.totalPages || 1);
            }
            else if (Array.isArray(res.data)) setUserReports(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchUserGrowth = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/user-growth?search=${search}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`, tokenConfig);
            if (res.data?.data) {
                setUserGrowth(res.data.data);
                if (res.data.summary) setUserGrowthSummary(res.data.summary);
                setTotalPages(res.data.paginationDetail?.totalPages || res.data.totalPages || res.data.pagination?.totalPages || 1);
            }
            else if (Array.isArray(res.data)) setUserGrowth(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchAstroPerformance = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}/admin/astrologer-performance?search=${search}&startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`, tokenConfig);
            if (res.data?.data) {
                setAstroPerformance(res.data.data);
                setTotalPages(res.data.paginationDetail?.totalPages || res.data.totalPages || res.data.pagination?.totalPages || 1);
            }
            else if (Array.isArray(res.data)) setAstroPerformance(res.data);
        } catch (error) { console.error(error); }
    };

    useEffect(() => {
        fetchSummary();
    }, []);

    useEffect(() => {
        setPage(1);
    }, [activeKey, search, startDate, endDate]);

    useEffect(() => {
        if (activeKey === 'status') fetchAstrologers();
        if (activeKey === 'carts') fetchCarts();
        if (activeKey === 'users') fetchUserReports();
        if (activeKey === 'growth') fetchUserGrowth();
        if (activeKey === 'performance') fetchAstroPerformance();
    }, [activeKey, search, startDate, endDate, page]);

    const activeAstroCount = summary?.onlineAstrologersCount || 0;
    const busyAstroCount = summary?.busyAstrologersCount || 0;
    const totalActiveAstrologers = summary?.activeAstrologersCount || (activeAstroCount + busyAstroCount);

    const toggleAstroService = async (id, service, currentValue) => {
        try {
            await axios.patch(`${process.env.REACT_APP_BASEURL}/admin/astrologer/force-busy/${id}`, {
                [service]: !currentValue
            }, tokenConfig);
            fetchAstrologers();
        } catch (error) { console.error(error); }
    };

    const handleExport = async () => {
        let endpoint = '';
        let filename = 'export.csv';
        if (activeKey === 'status') { endpoint = '/admin/online-astrologers'; filename = 'online-astrologers.csv'; }
        if (activeKey === 'carts') { endpoint = '/admin/cart-abandoned'; filename = 'abandoned-carts.csv'; }
        if (activeKey === 'users') { endpoint = '/admin/user-recharge-report'; filename = 'financial-reports.csv'; }
        if (activeKey === 'growth') { endpoint = '/admin/user-growth'; filename = 'user-growth.csv'; }
        if (activeKey === 'performance') { endpoint = '/admin/astrologer-performance'; filename = 'astro-performance.csv'; }

        if (!endpoint) return;

        try {
            const res = await axios.get(`${process.env.REACT_APP_BASEURL}${endpoint}?search=${search}&startDate=${startDate}&endDate=${endDate}`, tokenConfig);
            let data = res.data?.data || res.data;
            if (!Array.isArray(data)) return;

            if (data.length === 0) {
                alert("No data available to export");
                return;
            }

            const headers = Object.keys(data[0]);
            const csvRows = [];
            csvRows.push(headers.join(','));

            for (const row of data) {
                const values = headers.map(header => {
                    let val = row[header];
                    if (typeof val === 'object' && val !== null) val = JSON.stringify(val);
                    const escaped = ('' + (val ?? '')).replace(/"/g, '""');
                    return `"${escaped}"`;
                });
                csvRows.push(values.join(','));
            }

            const csvString = csvRows.join('\n');
            const blob = new Blob([csvString], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.setAttribute('hidden', '');
            a.setAttribute('href', url);
            a.setAttribute('download', filename);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error("Export failed:", error);
            alert("Failed to export data");
        }
    };

    const renderPagination = () => (
        <div className="d-flex justify-content-between align-items-center mt-3 px-2">
            <span className="small text-muted">Page {page} of {totalPages || 1}</span>
            <div className="d-flex gap-2">
                <Button variant="outline-primary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="outline-primary" size="sm" disabled={page >= (totalPages || 1)} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
        </div>
    );

    // ApexCharts Config
    const chartOptions = {
        chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#4f46e5', '#f43f5e'],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: [3, 2], dashArray: [0, 5] },
        xaxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr'] },
        tooltip: { theme: 'light' },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 90, 100] } },
    };
    const chartSeries = [
        { name: 'Registrations', data: [400, 300, 200, 278] },
        { name: 'Recharges', data: [240, 139, 980, 390] }
    ];

    return (
        <div className="bg-light pb-5 min-vh-100">
            {/* Header */}
            {/* <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom py-3 sticky-top shadow-sm">
                <Container fluid className="px-4">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary p-2 rounded-3 text-white shadow-sm">
                            <FaStar size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 font-weight-black">Jyotish Admin Panel</h5>
                            <p className="small text-muted mb-0">Management Portal</p>
                        </div>
                    </div>
                    <div className="ms-auto flex items-center gap-3">
                        <div className="bg-light rounded-pill p-1 pe-3 d-flex align-items-center gap-2">
                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-center"style={{ width: '32px', height: '32px' }}>A</div>
                            <span className="small ">Admin</span>
                        </div>
                    </div>
                </Container>
            </nav> */}

            <Container fluid className="px-4 py-4 mt-2">
                {/* Stats Row */}
                <Row className="g-4 mb-4">
                    <Col md={4}>
                        <StatCard title="New Registered Users" value={summary?.newUsers || "0"} icon={FaUsers} colorClass="bg-info" />
                    </Col>
                    <Col md={4}>
                        <StatCard title="Total Recharges" value={`₹ ${summary?.recharges || "0"}`} icon={FaCreditCard} colorClass="bg-warning" />
                    </Col>
                    <Col md={4}>
                        <StatCard title="Active Astrologers" value={totalActiveAstrologers} icon={FaUserCheck} colorClass="bg-success" />
                    </Col>
                    <Col md={4}>
                        <StatCard title="Abandoned Carts" value={summary?.abandonedCartsCount || "0"} icon={FaShoppingCart} colorClass="bg-danger" />
                    </Col>
                    <Col md={4}>
                        <StatCard title="Online Astrologers" value={activeAstroCount} icon={FaChartLine} colorClass="bg-success" />
                    </Col>
                    <Col md={4}>
                        <StatCard title="Busy Astrologers" value={busyAstroCount} icon={FaUserSlash} colorClass="bg-warning" />
                    </Col>
                </Row>

                {/* Main Content Card */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <Card.Header className="bg-white border-0 p-0">
                        <Tab.Container activeKey={activeKey} onSelect={(k) => setActiveKey(k || 'status')}>
                            <Nav variant="tabs" className="px-4 pt-2 border-0 bg-white">
                                <Nav.Item>
                                    <Nav.Link eventKey="status" className="py-3 font-bold border-0 border-bottom-3">
                                        ONLINE STATUS
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="carts" className="py-3 font-bold border-0 border-bottom-3">
                                        ABANDONED CARTS
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="users" className="py-3 font-bold border-0 border-bottom-3">
                                        FINANCIAL REPORTS
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="growth" className="py-3 font-bold border-0 border-bottom-3">
                                        USER GROWTH
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="performance" className="py-3 font-bold border-0 border-bottom-3">
                                        ASTRO PERFORMANCE
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>

                            <Card.Body className="p-4">
                                <Tab.Content>
                                    {/* TAB 1: Online Status */}
                                    <Tab.Pane eventKey="status">
                                        <FilterBar
                                            title="Astrologer Online Visibility"
                                            onSearch={setSearch}
                                            onStartDateChange={setStartDate}
                                            onEndDateChange={setEndDate}
                                            onExport={handleExport}
                                        />
                                        <Table responsive hover className="align-middle border-light">
                                            <thead className="bg-light text-muted small text-uppercase ">
                                                <tr>
                                                    <th>ID</th>
                                                    <th>NAME</th>
                                                    <th>PHONE</th>
                                                    <th>STATUS</th>
                                                    <th>LAST LOGIN</th>
                                                    <th className="text-center">FORCE CONTROL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {astrologers.map(a => (
                                                    <tr key={a._id || a.id}>
                                                        <td className="small  text-muted">{a._id || a.id}</td>
                                                        <td className="text-primary">{a.fullName || a.name}</td>
                                                        <td className="small">{a.mobileNumber || a.phone}</td>
                                                        <td>
                                                            <span className={`badge rounded-pill ${(a.computedStatus || a.status) === 'available' || (a.computedStatus || a.status) === 'Available' ? 'bg-success' : 'bg-warning'} text-white`}>
                                                                {a.computedStatus || a.status}
                                                            </span>
                                                        </td>
                                                        <td className="small text-muted italic">
                                                            {a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleString() : a.lastLogin}
                                                        </td>
                                                        <td>
                                                            <div className="d-flex justify-content-center gap-3">
                                                                {[
                                                                    { key: 'isChatEnabled', label: 'Chat', active: a.isChatEnabled !== undefined ? a.isChatEnabled : a.chatActive },
                                                                    { key: 'isCallEnabled', label: 'Call', active: a.isCallEnabled !== undefined ? a.isCallEnabled : a.callActive },
                                                                    { key: 'isAppointmentEnabled', label: 'Appointment', active: a.isAppointmentEnabled !== undefined ? a.isAppointmentEnabled : a.appointment }
                                                                ].map((srv, i) => (
                                                                    <div key={i} className="text-center d-flex flex-column align-items-center">
                                                                        <Form.Check
                                                                            type="switch"
                                                                            id={`switch-${a._id || a.id}-${srv.key}`}
                                                                            checked={srv.active}
                                                                            onChange={() => toggleAstroService(a._id || a.id, srv.key, srv.active)}
                                                                            className="mb-1 custom-switch-lg"
                                                                        />
                                                                        <div className="small text-muted font-weight-bold" style={{ fontSize: '10px' }}>{srv.label}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination()}
                                    </Tab.Pane>

                                    {/* TAB 2: Abandoned Carts */}
                                    <Tab.Pane eventKey="carts">
                                        <FilterBar title="Abandoned Carts Analysis" onSearch={setSearch} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onExport={handleExport} />
                                        <Table responsive hover className="align-middle">
                                            <thead className="bg-light text-muted small text-uppercase ">
                                                <tr>
                                                    <th>DATE</th>
                                                    <th>PHONE</th>
                                                    <th>NAME</th>
                                                    <th>VALUE</th>
                                                    <th>STREAM</th>
                                                    <th className="text-end">ACTION</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {carts.map(c => (
                                                    <tr key={c._id || c.id}>
                                                        <td className="small text-muted">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : c.date}</td>
                                                        <td className="">{c.mobileNumber || c.phone}</td>
                                                        <td>{c.name || c.userName}</td>
                                                        <td className="text-primary ">₹ {c.orderValue !== undefined ? c.orderValue : c.value}</td>
                                                        <td><span className={`badge ${c.stream === 'course' || c.stream === 'Course' ? 'bg-purple' : 'bg-blue'} opacity-75`}>{c.stream}</span></td>
                                                        <td className="text-end">
                                                            <Button variant="light" size="sm" className="rounded-circle border" onClick={() => setSelectedCart(c)}>
                                                                <FaEye className="text-primary" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination()}
                                    </Tab.Pane>

                                    {/* TAB 3: User Registration & Recharge Report */}
                                    <Tab.Pane eventKey="users">
                                        <FilterBar title="User Registration & Recharge Report" onSearch={setSearch} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onExport={handleExport} />
                                        <Table responsive hover className="align-middle text-nowrap">
                                            <thead className="bg-light text-muted small text-uppercase ">
                                                <tr>
                                                    <th>NAME & ID</th>
                                                    <th className="text-center">RECHARGES</th>
                                                    <th>TOTAL AMOUNT</th>
                                                    <th>WALLET BALANCE</th>
                                                    <th>CREATED AT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userReports.map(u => (
                                                    <tr key={u._id || u.id}>
                                                        <td>
                                                            <div className="">{u.fullName || u.name || u.mobileNumber}</div>
                                                            <div className="small text-muted">{u._id || u.id}</div>
                                                        </td>
                                                        <td className="text-center  bg-light-blue">{u.rechargeCount !== undefined ? u.rechargeCount : u.rechargesCount}</td>
                                                        <td className="text-success">₹ {(u.totalRechargeAmount !== undefined ? u.totalRechargeAmount : u.totalAmount)?.toLocaleString()}</td>
                                                        <td className="text-danger">₹ {u.walletBalance?.toLocaleString()}</td>
                                                        <td className="small text-muted">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : u.createdAt}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination()}
                                    </Tab.Pane>

                                    {/* TAB 4: User Growth */}
                                    <Tab.Pane eventKey="growth">
                                        <FilterBar title="Platform Expansion Metrics" onSearch={setSearch} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onExport={handleExport} />
                                        <Row className="g-4 mb-4">
                                            <Col md={6}>
                                                <div className="p-4 rounded-4 bg-gradient-primary text-white shadow-sm position-relative">
                                                    <div className="d-flex justify-content-between">
                                                        <FaMobileAlt size={24} className="opacity-50" />
                                                        <span className="small  bg-white-20 rounded px-2">ANDROID</span>
                                                    </div>
                                                    <h2 className="mt-3 font-weight-black mb-0">{userGrowthSummary?.platformBreakdown?.android || 0}</h2>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="p-4 rounded-4 bg-gradient-info text-white shadow-sm position-relative">
                                                    <div className="d-flex justify-content-between">
                                                        <FaLaptop size={24} className="opacity-50" />
                                                        <span className="small  bg-white-20 rounded px-2">IOS</span>
                                                    </div>
                                                    <h2 className="mt-3 font-weight-black mb-0">{userGrowthSummary?.platformBreakdown?.ios || 0}</h2>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Table responsive hover className="align-middle">
                                            <thead className="bg-light text-muted small text-uppercase ">
                                                <tr>
                                                    <th>NAME</th>
                                                    <th>PHONE</th>
                                                    <th>EMAIL</th>
                                                    <th>PLATFORM</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {userGrowth.map(u => (
                                                    <tr key={u._id || u.id}>
                                                        <td className="">{u.fullName || u.name || u.mobileNumber}</td>
                                                        <td>{u.mobileNumber || u.phone}</td>
                                                        <td className="text-muted small">{u.email || '-'}</td>
                                                        <td>
                                                            <span className={`badge ${u.platform?.toLowerCase() === 'android' ? 'bg-success' : 'bg-info'} opacity-75`}>
                                                                {u.platform || 'Unknown'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination()}
                                    </Tab.Pane>

                                    {/* TAB 5: Astro Performance */}
                                    <Tab.Pane eventKey="performance">
                                        <FilterBar title="Astrologer Performance & Rating" onSearch={setSearch} onStartDateChange={setStartDate} onEndDateChange={setEndDate} onExport={handleExport} />
                                        <Table responsive hover className="align-middle text-nowrap">
                                            <thead className="bg-light text-muted small text-uppercase ">
                                                <tr>
                                                    <th>NAME</th>
                                                    <th>ID</th>
                                                    <th className="text-center">CONNECT REQ.</th>
                                                    <th className="text-center">SUCCESSFUL</th>
                                                    <th>ORDER VAL (₹)</th>
                                                    <th className="text-center">RATING CNT.</th>
                                                    <th>AVG RATING</th>
                                                    <th>1ST RESP</th>
                                                    <th>AVG CONSULT</th>
                                                    <th className="text-danger text-center">REJECTED</th>
                                                    <th className="text-warning text-center">UNANSWERED</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {astroPerformance.map(p => (
                                                    <tr key={p._id || p.id}>
                                                        <td className="">{p.fullName || p.name}</td>
                                                        <td className="small text-muted">{p._id || p.id}</td>
                                                        <td className="text-center">{p.totalOrders !== undefined ? p.totalOrders : p.connectRequests}</td>
                                                        <td className="text-center text-success ">{p.completedConsultations !== undefined ? p.completedConsultations : p.successfulOrders}</td>
                                                        <td className="text-primary">₹ {(p.totalEarnings !== undefined ? p.totalEarnings : p.orderValue)?.toLocaleString()}</td>
                                                        <td className="text-center">{p.reviewCount !== undefined ? p.reviewCount : p.ratingCounts}</td>
                                                        <td>
                                                            <div className="d-flex align-items-center gap-1 ">
                                                                <FaStar className="text-warning mb-1" size={12} /> {(p.averageRating !== undefined ? p.averageRating : p.avgRating)?.toFixed ? (p.averageRating !== undefined ? p.averageRating : p.avgRating)?.toFixed(1) : (p.averageRating !== undefined ? p.averageRating : p.avgRating)}
                                                            </div>
                                                        </td>
                                                        <td className="small text-muted italic">{p.avgResponseTimeMinutes !== undefined ? `${p.avgResponseTimeMinutes} mins` : p.firstResponseTime}</td>
                                                        <td className="small text-muted italic">{p.averageConsultationDuration !== undefined ? `${p.averageConsultationDuration} mins` : p.avgConsultationTime}</td>
                                                        <td className="text-center text-danger  bg-danger-low">{p.rejectedOrders}</td>
                                                        <td className="text-center text-warning  bg-warning-low">{p.unansweredOrders}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                        {renderPagination()}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Tab.Container>
                    </Card.Header>
                </Card>

                {/* Bonus: Growth Chart with ApexCharts */}
                {/* <Card className="mt-4 border-0 shadow-sm rounded-4 mb-5">
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <h6 className="mb-0 font-weight-black">Platform Trends</h6>
                                <p className="small text-muted mb-0">Monthly Registration vs Recharge</p>
                            </div>
                        </div>
                        <Chart options={chartOptions} series={chartSeries} type="area"height={350} />
                    </Card.Body>
                </Card> */}
            </Container>

            {/* MODAL: Detail View */}
            <Modal show={!!selectedCart} onHide={() => setSelectedCart(null)} centered size="lg">
                <Modal.Header closeButton className="border-0 bg-primary text-white p-4">
                    <Modal.Title className="font-weight-black">
                        <FaShoppingCart className="me-2" /> Abandoned Cart Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 bg-light">
                    {selectedCart && (
                        <Row className="g-4">
                            <Col md={6}>
                                <div className="bg-white p-4 rounded-4 shadow-sm border h-100">
                                    <label className="text-uppercase small text-muted  d-block mb-1">User Information</label>
                                    <h5 className="font-weight-black text-primary">{selectedCart.name || selectedCart.userName}</h5>
                                    <p className="mb-0 ">{selectedCart.mobileNumber || selectedCart.phone}</p>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="bg-white p-4 rounded-4 shadow-sm border h-100">
                                    <label className="text-uppercase small text-muted  d-block mb-1">Order Summary</label>
                                    <div className="d-flex justify-content-between align-items-end">
                                        <h3 className="font-weight-black mb-0 text-success">₹ {selectedCart.orderValue !== undefined ? selectedCart.orderValue : selectedCart.value}</h3>
                                        <span className="badge bg-light text-muted border">{selectedCart.createdAt ? new Date(selectedCart.createdAt).toLocaleDateString() : selectedCart.date}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="bg-white p-4 rounded-4 shadow-sm border">
                                    <label className="text-uppercase small text-muted  d-block mb-2">Item Details</label>
                                    <div className="p-3 bg-light rounded-3 border">
                                        <p className="mb-0 ">{selectedCart.viewOrder?.label || selectedCart.details}</p>
                                    </div>
                                    <div className="mt-3">
                                        <span className="badge bg-primary me-2 px-3 py-2 rounded-3">{selectedCart.stream}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                {/* <Modal.Footer className="border-0 p-4 pt-0 bg-light">
                    <Button variant="secondary" onClick={() => setSelectedCart(null)} className="px-5 rounded-pill ">
                        Close Analysis
                    </Button>
                    <Button variant="primary" className="px-5 rounded-pill ">
                        Send Reminder
                    </Button>
                </Modal.Footer> */}
            </Modal>

            <style>{`
        .font-weight-black { font-weight: 900; }
        .modal-backdrop.show {
            opacity: 0.7 !important;
            backdrop-filter: blur(5px);
        }
        .er { font-weight: 800; }
        . { font-weight: 700; }
        .italic { font-style: italic; }
        .border-bottom-3 { border-bottom: 3px solid transparent !important; }
        .nav-link.active { border-bottom-color: var(--bs-primary) !important; color: var(--bs-primary) !important; }
        .bg-gradient-primary { background: linear-gradient(45deg, #4f46e5, #7c3aed); }
        .bg-gradient-info { background: linear-gradient(45deg, #0ea5e9, #2563eb); }
        .bg-white-20 { background-color: rgba(255,255,255,0.2); }
        .bg-light-blue { background-color: #f0f7ff; }
        .bg-danger-low { background-color: #fff1f2; }
        .bg-warning-low { background-color: #fffbeb; }
        .bg-blue { background-color: #0d6efd; color: white; }
        .bg-purple { background-color: #6f42c1; color: white; }
      `}</style>
        </div>
    );
}
